/* ========================================================================== *\
___________       __         .__
\_   _____/ _____/  |________|__| ____
 |    __)_ /    \   __\_  __ \  |/  _ \
 |        \   |  \  |  |  | \/  (  <_> )
/_______  /___|  /__|  |__|  |__|\____/
        \/     \/
\* ========================================================================== */
// เลือกกราฟิก AI ของแคมเปญมาเป็นภาพหลัก (หน้ารูปภาพของแคมเปญ · 26 ต.ค. 2569 · เจ้าของงานขอ
// "กดเลือกรูปแล้วเลือกจากแกลเลอรีที่เคยเจน แล้วซิงค์กับหน้าข้อมูลแคมเปญ")
//   อ่าน OPTIONS /campaign_graphic/by_campaign · ตั้งภาพหลัก OPTIONS /campaign_graphic/set_main {id}
//   ภาพที่ยังไม่เก็บเข้ารูปแคมเปญ server เก็บให้ก่อน · หน้าข้อมูลกับหน้ารูปภาพใช้ภาพหลักตัวเดียวกัน
import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import ButtonBase from '@mui/material/ButtonBase';
import CircularProgress from '@mui/material/CircularProgress';
import Icon from '@mui/material/Icon';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';
import { FetchData } from 'services/Request';
import * as Func from 'services/Function';

const ERRORS = {
  permission_denied: 'ต้องมีสิทธิ์สร้าง/แก้/เก็บ/ลบภาพ AI ถึงจะตั้งภาพหลักจากกราฟิกได้',
  ai_result_not_ready: 'ภาพนี้ยังไม่พร้อม โหลดหน้าใหม่แล้วลองอีกครั้ง',
  ai_already_approved: 'ภาพนี้เคยเก็บไว้แล้วแต่หารูปไม่เจอ โหลดหน้าใหม่แล้วลองอีกครั้ง',
  ai_copy_failed: 'คัดลอกภาพเข้ารูปแคมเปญไม่สำเร็จ แจ้งไอที',
  not_owner: 'ภาพนี้เป็นของคนอื่น — ตั้งได้เฉพาะเจ้าของภาพหรือผู้มีสิทธิ์ดูงาน AI ของทุกคน',
};

export default function CampaignGraphicPicker({
  open,
  token,
  campaignId,
  mainImageId,
  onClose,
  onDone,
  onCreate,
}) {
  const [list, setList] = useState(null); // null = กำลังโหลด
  const [mine, setMine] = useState(true); // server ให้เฉพาะของตัวเอง (ไม่ใช่ผู้ดูแล AI)
  const [canSet, setCanSet] = useState(true); // ตั้งภาพหลัก/ไปสร้างกราฟิกต้องมีสิทธิ์ ai_generate (รีเช็ค 27 ต.ค. 2569)
  const [sel, setSel] = useState(0);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const alive = useRef(true);

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  useEffect(() => {
    if (!open) return;
    setList(null);
    setSel(0);
    setErr('');
    /* ขอของทุกคน — server ให้เฉพาะผู้ดูแล AI · คนอื่นได้เฉพาะภาพที่ตัวเองเจน (27 ต.ค. 2569) */
    FetchData('OPTIONS', '/campaign_graphic/by_campaign', token, {
      campaign_id: campaignId,
      scope: 'all',
      _t: Date.now(),
    })
      .then((r) => {
        if (!alive.current) return;
        if (r.status !== 'ok') {
          setList([]);
          setErr('โหลดกราฟิกไม่สำเร็จ ลองอีกครั้ง');
          return;
        }
        setMine(r.scope !== 'all');
        setCanSet(r.can_generate !== 'N');
        setList((r.data || []).filter((g) => g.status === 'SUCCESS' && g.image_url));
      })
      .catch(() => {
        if (!alive.current) return;
        setList([]);
        setErr('โหลดกราฟิกไม่สำเร็จ ลองอีกครั้ง');
      });
  }, [open, campaignId]);

  const save = () => {
    setBusy(true);
    setErr('');
    FetchData('OPTIONS', '/campaign_graphic/set_main', token, { id: sel, _t: Date.now() })
      .then((r) => {
        if (!alive.current) return;
        setBusy(false);
        if (r.status === 'ok') onDone(r);
        else setErr(ERRORS[r.msg] || 'ตั้งภาพหลักไม่สำเร็จ ลองอีกครั้ง');
      })
      .catch(() => {
        if (!alive.current) return;
        setBusy(false);
        setErr('ตั้งภาพหลักไม่สำเร็จ ลองอีกครั้ง');
      });
  };

  const isMain = (g) =>
    Number(g.approved_image_id) > 0 && Number(g.approved_image_id) === mainImageId;

  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onClose}
      fullWidth
      maxWidth='md'
      aria-labelledby='campaign-graphic-picker-title'
    >
      <DialogTitle id='campaign-graphic-picker-title'>
        เลือกจากกราฟิก AI
        {list && list.length > 0 && (
          <MDTypography component='span' variant='button' color='text' sx={{ ml: 1, opacity: 0.7 }}>
            {list.length} ภาพ
          </MDTypography>
        )}
      </DialogTitle>
      <DialogContent dividers>
        {list === null && (
          <MDBox display='flex' justifyContent='center' py={4}>
            <CircularProgress size={28} />
          </MDBox>
        )}
        {list && list.length === 0 && !err && (
          <MDBox textAlign='center' py={3}>
            <MDTypography variant='body2' color='text'>
              {mine ? 'คุณยังไม่ได้สร้างกราฟิก AI ในแคมเปญนี้' : 'แคมเปญนี้ยังไม่มีกราฟิก AI'}
            </MDTypography>
            {canSet && (
              <MDButton
                variant='gradient'
                color='info'
                size='small'
                sx={{ mt: 1.5 }}
                onClick={onCreate}
              >
                <Icon>auto_awesome</Icon>&nbsp;ไปสร้างกราฟิก AI
              </MDButton>
            )}
          </MDBox>
        )}
        {list && list.length > 0 && (
          <MDBox
            display='grid'
            gap={1.5}
            sx={{ gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))' }}
          >
            {list.map((g) => {
              const on = sel === Number(g.id);
              const main = isMain(g);
              return (
                <ButtonBase
                  key={g.id}
                  aria-pressed={on}
                  aria-label={`เลือกกราฟิก #${g.id}${main ? ' · ภาพหลักตอนนี้' : ''}`}
                  onClick={() => setSel(Number(g.id))}
                  disabled={busy}
                  sx={{
                    display: 'block',
                    textAlign: 'left',
                    borderRadius: 2,
                    border: '2px solid',
                    borderColor: on ? 'info.main' : 'grey.300',
                    overflow: 'hidden',
                    bgcolor: '#fff',
                    '&:hover': { borderColor: 'info.main' },
                    '&.Mui-focusVisible': { outline: '2px solid', outlineColor: 'info.main' },
                  }}
                >
                  <MDBox
                    position='relative'
                    sx={{ aspectRatio: '1 / 1', bgcolor: 'grey.100', display: 'flex' }}
                  >
                    <MDBox
                      component='img'
                      src={g.image_url}
                      alt=''
                      loading='lazy'
                      sx={{ width: '100%', height: '100%', objectFit: 'contain' }}
                    />
                    {on && (
                      <Icon
                        sx={{
                          position: 'absolute',
                          top: 6,
                          right: 6,
                          color: 'info.main',
                          bgcolor: '#fff',
                          borderRadius: '50%',
                        }}
                      >
                        check_circle
                      </Icon>
                    )}
                  </MDBox>
                  <MDBox px={1} py={0.75}>
                    <MDTypography
                      variant='caption'
                      fontWeight='bold'
                      color={main ? 'info' : 'dark'}
                      display='block'
                    >
                      {main ? 'ภาพหลักตอนนี้' : g.approved === 'Y' ? 'เก็บแล้ว' : `กราฟิก #${g.id}`}
                      {g.aspect_ratio ? ` · ${g.aspect_ratio}` : ''}
                    </MDTypography>
                    <MDTypography variant='caption' color='text' display='block'>
                      {Func.dateDisplay(String(g.created_at || '').replace(' ', 'T'))}
                    </MDTypography>
                  </MDBox>
                </ButtonBase>
              );
            })}
          </MDBox>
        )}
        {list && !canSet && (
          <MDTypography variant='caption' color='text' display='block' mt={1.5} role='status'>
            บัญชีนี้ดูได้อย่างเดียว · ตั้งภาพหลักจากกราฟิก AI ต้องมีสิทธิ์สร้างภาพ AI —
            ขอจากผู้ดูแลระบบ
          </MDTypography>
        )}
        {err && (
          <MDTypography variant='caption' color='error' display='block' mt={1.5} role='alert'>
            {err}
          </MDTypography>
        )}
      </DialogContent>
      <DialogActions>
        <MDButton variant='outlined' color='dark' size='small' onClick={onClose} disabled={busy}>
          ปิด
        </MDButton>
        {list && list.length > 0 && canSet && (
          <MDButton
            variant='gradient'
            color='info'
            size='small'
            disabled={!sel || busy}
            onClick={save}
          >
            {busy ? 'กำลังตั้ง…' : 'ตั้งเป็นภาพหลัก'}
          </MDButton>
        )}
      </DialogActions>
    </Dialog>
  );
}

CampaignGraphicPicker.defaultProps = { mainImageId: 0 };
CampaignGraphicPicker.propTypes = {
  open: PropTypes.bool.isRequired,
  token: PropTypes.string.isRequired,
  campaignId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  mainImageId: PropTypes.number, // campaigns.image_id — ป้าย "ภาพหลักตอนนี้"
  onClose: PropTypes.func.isRequired,
  onDone: PropTypes.func.isRequired, // ตั้งสำเร็จ → หน้าแคมเปญโหลดรูปใหม่
  onCreate: PropTypes.func.isRequired, // ยังไม่มีกราฟิก → ไปแท็บกราฟิก AI
};
